import React from "react";
import clsx from "clsx";
import { useParams, useHistory, Prompt } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@SonarCX/hooks/redux";
import {
  AddCircle,
  ArrowLeft2,
  Eye,
  Hierarchy,
  More,
  Send,
  EyeSlash,
} from "iconsax-react";
import { useTheme } from "@mui/material";
import { useDrop } from "react-dnd";

import PermissionErrorPage from "../PermissionError";
import AgentPage from "../../components/templates/AgentPage";
import LoadingSpinner from "../../components/atoms/LoadingSpinner";
import CollapseButton from "../../components/atoms/CollapseButton";
import SubTaskArea from "../../components/molecules/ChatModals/TaskModal/SubTaskArea";
import TaskDescription from "../../components/molecules/ChatModals/TaskModal/TaskDescription";
import TaskComments from "../../components/molecules/ChatModals/TaskModal/TaskComments";
import HeaderButtons from "../../components/molecules/ChatModals/TaskModal/HeaderButtons";
import SaveButton from "../../components/atoms/Button2";
import ButtonMenu from "../../components/molecules/ButtonMenu";
import HeaderInfo from "../../components/molecules/ChatModals/TaskModal/NoPermissions/HeaderInfo";
import TaskArrows from "../../components/molecules/TaskArrows";
import TaskTagsSection from "../../components/molecules/ChatModals/TaskModal/TaskTagsSection";
import CombinedTaskMeta from "./CombinedTaskMeta";
import CustomFieldsSection from "./CustomFieldsSection";
import FavoriteButton from "@SonarCX/components/atoms/FavoriteButton";
import PinButton from "@SonarCX/components/atoms/PinButton";
import ShareTaskModal from "@SonarCX/components/molecules/ShareTaskModal";
import EmailDrop from "@SonarCX/components/molecules/EmailHolder/EmailDrop";
import Tooltip from "@SonarCX/components/atoms/Tooltip";

import { EMAIL_DRAG_TYPE } from "@SonarCX/components/molecules/EmailHolder/Email";
import { FILE_EXTENSION } from "@SonarCX/components/molecules/TaskNote";
import { Routes } from "@SonarCX/Routes";
import { isUUID } from "@SonarCX/utilities";
import { isEmpty } from "@SonarCX/components/organisms/LexicalEditor";
import { searchUser } from "@SonarCX/lib/api";
import { getSubtasks, getTask } from "@SonarCX/lib/api";
import useAccess, { PERMS } from "@SonarCX/hooks/useAccess";
import { DEFAULT_EMBEDS, useTaskAPI } from "@SonarCX/providers/TaskAPIProvider";
import { selectCategory, fetchCategories } from "@SonarCX/slices/category";
import { useUserAPI } from "@SonarCX/providers/UserAPIProvider";
import { useAgent } from "@SonarCX/providers/AgentProvider";
import { fetchTypes, selectTaskType } from "@SonarCX/slices/taskType";
import { useAreYouSure } from "@SonarCX/providers/AreYouSureProvider";
import { useTaskComments } from "@SonarCX/providers/TaskCommentsProvider";
import { useTaskPageState } from "@SonarCX/providers/TaskPageStateProvider";
import { useBreakpoint } from "@SonarCX/providers/BreakpointProvider";
import { useTaskList } from "@SonarCX/providers/TaskListProvider";
import { fetchStatus, selectTaskStatus } from "@SonarCX/slices/taskStatus";
import { fetchPriorities, selectTaskPriority } from "@SonarCX/slices/priority";
import { saveMeta } from "@SonarCX/slices/taskMetaCache";
import { fetchTeams, selectTeams } from "@SonarCX/slices/teamsSlice";
import { selectExtensionAPI } from "@SonarCX/slices/extensionAPI";
import { AddErrorAlert, AddSuccessAlert } from "@SonarCX/slices/alert";
import { useAuth } from "@SonarCX/providers/AuthProvider";
import { useTaskSWRMutateCache } from "@SonarCX/providers/TaskSWRMutateCache";
import { useEnvironment } from "@SonarCX/providers/EnvironmentProvider";
import { addFavorite, removeFavorite } from "@SonarCX/slices/favorites";
import useCustomFieldGroups from "@SonarCX/hooks/useCustomFieldGroups";
import useTaskPermissions from "@SonarCX/hooks/permissions/useTaskPermissions";
import useUserPermissions from "@SonarCX/hooks/permissions/useUserPermissions";
import useFavorites from "@SonarCX/hooks/useFavorites";
import useStyles from "./styles";
import useDetectNihalife from "@SonarCX/hooks/useDetectInstance/useDetectNihalife";
import useSavedValue from "@SonarCX/hooks/useSavedValue";

const ATTACH_FILE = "Attach a file";
const ATTACH_AUDIO = "Attach voice note";
const noop = () => {};
const formatTask = (vals) => {
  const newVals = { ...vals };

  Object.entries(vals).forEach(([prop, val]) => {
    switch (prop) {
      case "assignee_ids":
        newVals["primary"] = val;
        break;
      case "category_id":
        newVals["section"] = val;
        break;
      case "concerning_user_id":
        newVals["client"] = val;
        break;
      case "created_at":
        newVals["created_at"] = val ?? new Date();
        break;
      case "created_by":
        newVals["created_by"] = val;
        break;
      case "due_date":
        newVals["due_date"] = val ?? new Date();
        break;
      case "priority_id":
        newVals["priority"] = val;
        break;
      case "status_id":
        newVals["status"] = val;
        break;
      case "type_id":
        newVals["type"] = val;
        break;
      case "title":
        newVals["title"] = val;
        break;
      case "watcher_ids":
        newVals["watchers"] = val;
        break;
      case "details":
        newVals["content"] = val;
        break;
      case "tags":
        newVals["tags"] = val;
        break;
      case "sub_tasks":
        newVals["subtasks"] = val;
        break;
      case "media_ids":
        // Copy media and clear ids to prevent duplicate file uploads
        newVals["file"] = vals.media ?? [];
        newVals["media_ids"] = [];
        break;
      case "updated_at":
        newVals["updated_at"] = val;
        break;
      case "closed_at":
        newVals["closed_at"] = val;
        break;
      case "sort_order":
        newVals["sort_order"] = +val;
        break;
      case "owner_id":
        newVals["owner_id"] = val;
        break;
      default:
        break;
    }
  });

  return newVals;
};

const DELETE = "Delete";
const COPY = "Copy Link";
const DUPLICATE = "Duplicate";
const SHARE = "Share";
const COMPLETE = "Mark Complete";
const FAVOURITE = "Favourite";
const UNFAVOURITE = "Remove Favourite";
const CLOSE = "Close";
const SAVE = "Save";
const CONVERT_TO_MAIN_TASK = "Convert to Main Task";

const MAX_DESCRIPTION_LENGTH = 3000;

const TaskDetailsPage = () => {
  const theme = useTheme();
  const history = useHistory();
  const { id } = useParams();

  const dispatch = useAppDispatch();
  const {
    statuses,
    loaded: taskStatusLoaded,
    loading: taskStatusLoading,
  } = useAppSelector(selectTaskStatus);
  const {
    priorities,
    loaded: prioritiesLoaded,
    loading: prioritiesLoading,
  } = useAppSelector(selectTaskPriority);
  const {
    typesValues: types,
    loaded: typesLoaded,
    loading: typesLoading,
  } = useAppSelector(selectTaskType);
  const {
    categoriesValues: categories,
    loaded: categoriesLoaded,
    loading: categoriesLoading,
  } = useAppSelector(selectCategory);
  const {
    teams,
    loaded: teamsLoaded,
    loading: teamsLoading,
  } = useAppSelector(selectTeams);
  const { emails } = useAppSelector(selectExtensionAPI);

  const {
    tasks,
    updateTask,
    deleteSingleTask,
    addComment,
    pushTask,
    markCloseTask,
    handleTaskUpsert,
    startRecording,
    stopRecording,
    isRecording,
    inputFileRef,
  } = useTaskAPI();
  const { loadUser, uploadUser } = useUserAPI();
  const { currentUser } = useAgent();
  const { setAction, setReason, setV2, setTitle, setSubtext } = useAreYouSure();
  const { comments: allComments, loadComments } = useTaskComments();
  const hasPermission = useAccess();
  const { setLastViewed, tab } = useTaskPageState();
  const { isSmall } = useBreakpoint();
  const { favoriteTasks } = useFavorites();
  const { reset: resetList, popFrame, popOnBack } = useTaskList();
  const { hasBasic: canSearchUser } = useUserPermissions();
  const { isLoggedIn, isLoading } = useAuth();
  const { updateTasks } = useTaskSWRMutateCache();
  const { user } = useEnvironment();
  const { isNihalife } = useDetectNihalife();

  const [showClosedTask, setShowClosedTask] = useSavedValue({
    key: "showClosedTask",
    defaultValue: "true",
  });

  const [values, setValues] = React.useState({});
  const { hasFields: hasCustomFields } = useCustomFieldGroups(
    "task",
    values.type_id,
  );

  const [taskFound, setTaskFound] = React.useState(false);
  const [subtaskTitle, setSubtaskTitle] = React.useState("");
  const [pending, setPending] = React.useState([]);
  const [existingSubtasks, setExistingSubtasks] = React.useState([]);
  const [showInput, setShowInput] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);
  const [pendingAdd, setPendingAdd] = React.useState([]);
  const [showUpdateMsg, setShowUpdateMsg] = React.useState(false);
  const [modifiedTask, setModifiedTask] = React.useState(true);
  const [comments, setComments] = React.useState([]);
  const [detailsExpanded, setDetailsExpanded] = React.useState(true);
  const [customFieldsExpanded, setCustomFieldsExpanded] = React.useState(true);
  const [tagsExpanded, setTagsExpanded] = React.useState(true);
  const [subAreaExpanded, setSubAreaExpanded] = React.useState(true);
  const [descriptionExpanded, setDescriptionExpanded] = React.useState(true);
  const [commentsExpanded, setCommentsExpanded] = React.useState(true);
  const [parentName, setParentName] = React.useState(null);
  const [changed, setChanged] = React.useState(false);
  const [shouldSave, setShouldSave] = React.useState(false);
  const [skipSaveAlert, setSkipSaveAlert] = React.useState(false);
  const [navigate, setNavigate] = React.useState(null);
  const [task, setTask] = React.useState({});
  const [newUserName, setNewUserName] = React.useState(null);
  const [meta, setMeta] = React.useState([]);
  const [showShare, setShowShare] = React.useState(false);
  const menuRef = React.useRef();
  const isSubTask = React.useMemo(() => Boolean(parentName), [parentName]);

  const isFavorite = React.useMemo(() => {
    if (!task.id) return false;

    return favoriteTasks.some((fav) => fav.item_id === task.id);
  }, [favoriteTasks, task]);

  const { canUpdate, canEdit, canClose, canView } = useTaskPermissions(task);

  // This task is closable if all subtasks are complete
  const canComplete = React.useMemo(() => {
    if (!existingSubtasks.length) {
      return true;
    }

    return existingSubtasks.reduce(
      (acc, cur) => acc && Boolean(cur.completed_at),
      true,
    );
  }, [existingSubtasks]);

  const hasAllSubtasksClosed = React.useMemo(() => {
    if (!existingSubtasks.length) {
      return true;
    }

    return existingSubtasks.reduce(
      (acc, cur) => acc && Boolean(cur.closed_at),
      true,
    );
  }, [existingSubtasks]);

  const canAddClient = React.useMemo(() => {
    if (hasPermission([PERMS.ContactsAudit, PERMS.ContactsOwn])) {
      return true;
    }

    return false;
  }, [hasPermission]);

  // Task is fully done if the status is complete
  const isComplete = React.useMemo(() => {
    if (!values?.status) {
      return false;
    }

    const fullStatus = statuses.find((status) => status.id === values.status);
    if (!fullStatus) {
      return false;
    }

    return fullStatus.base_status === "completed";
  }, [values, statuses]);

  const classes = useStyles();

  React.useEffect(() => {
    setLastViewed(id);
  }, [setLastViewed, id]);

  React.useEffect(() => {
    if (!taskStatusLoaded && !taskStatusLoading) {
      dispatch(fetchStatus());
    }
  }, [taskStatusLoaded, taskStatusLoading, dispatch]);

  React.useEffect(() => {
    if (!prioritiesLoaded && !prioritiesLoading) {
      dispatch(fetchPriorities());
    }
  }, [prioritiesLoaded, prioritiesLoading, dispatch]);

  React.useEffect(() => {
    if (!typesLoaded && !typesLoading) {
      dispatch(fetchTypes());
    }
  }, [typesLoaded, typesLoading, dispatch]);

  React.useEffect(() => {
    if (!categoriesLoaded && !categoriesLoading) {
      dispatch(fetchCategories());
    }
  }, [categoriesLoaded, categoriesLoading, dispatch]);

  React.useEffect(() => {
    if (!teamsLoaded && !teamsLoading) {
      dispatch(fetchTeams());
    }
  }, [teamsLoaded, teamsLoading, dispatch]);

  const disableComplete = React.useMemo(
    () => !canComplete || isComplete || (!canEdit && !canUpdate),
    [canComplete, isComplete, canEdit, canUpdate],
  );

  const shouldShowClose = React.useMemo(
    () => disableComplete && isComplete && canClose,
    [disableComplete, isComplete, canClose],
  );

  const menuOptions = React.useMemo(() => {
    const allOpts = [];

    if (isSmall) {
      allOpts.push(SAVE);
    }

    allOpts.push(DUPLICATE);

    allOpts.push(COPY);

    allOpts.push(SHARE);

    if (isSubTask) {
      allOpts.push(CONVERT_TO_MAIN_TASK);
    }

    if (isSmall) {
      if (isFavorite) {
        allOpts.push(UNFAVOURITE);
      } else {
        allOpts.push(FAVOURITE);
      }

      if (!disableComplete) {
        allOpts.push(COMPLETE);
      }

      if (shouldShowClose) {
        allOpts.push(CLOSE);
      }
    }

    allOpts.push(DELETE);

    return allOpts;
  }, [isSmall, isFavorite, disableComplete, shouldShowClose, isSubTask]);

  const detailsMenuOpts = React.useMemo(() => [ATTACH_FILE, ATTACH_AUDIO], []);

  // Get task details from cache or fetch subtask on refresh, also load comments
  React.useEffect(() => {
    if (taskFound) {
      return;
    }

    // Need to load on demand to stop this task from caching
    getTask(id, [...DEFAULT_EMBEDS, "parent"])
      .then((task) => {
        setTaskFound(true);
        setNotFound(false);
        const formatted = formatTask(task);
        setValues(formatted);
        setTask(formatted);
        loadComments(task.id);
        pushTask(task, true);
      })
      .catch((err) => {
        console.error("Error loading task !!!", err);
        setNotFound(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskFound, id, loadComments]);

  React.useEffect(() => {
    const comments = allComments[id];
    if (comments) {
      setComments(Object.values(comments));
    }
  }, [allComments, id]);

  // Update values if they change from a socket event
  React.useEffect(() => {
    const task = tasks[id];
    if (!task) {
      return;
    }

    setValues(formatTask(task));
  }, [tasks, id]);

  // Reset on id change
  React.useEffect(() => {
    setTaskFound(false);
  }, [id]);

  // Load parent task/get name
  React.useEffect(() => {
    if (!taskFound || !values.parent_id) {
      setParentName(null);
      return;
    }

    if (values.parent) {
      setParentName(values.parent.title);
    }
  }, [taskFound, values.parent_id, values.parent]);

  // Load users of comments
  React.useEffect(() => {
    comments.forEach((comment) => loadUser(comment.user_id));
  }, [comments, loadUser]);

  const handleChange = React.useCallback(
    (prop) => (e) => {
      // removing updated task modal and enabling update btn when edited
      if (!["content", "title"].includes(prop)) {
        setShouldSave(true);
      }
      setShowUpdateMsg(false);
      setModifiedTask(false);
      let val;
      if (prop === "due_date") {
        val = e.toDate();
      } else if (prop === "file" || prop === "content") {
        val = e;
      } else if (prop === "watchers") {
        let editWatchers = [];
        if (e.target.action === "clear") {
          editWatchers = [...values.watchers];
          const index = editWatchers.indexOf(e.target.value);
          editWatchers.splice(index, 1);
          val = editWatchers;
        } else {
          val = editWatchers = [...values.watchers, e.target.value];
        }
      } else if (prop === "primary") {
        let ids = [];
        if (e.target.action === "clear") {
          ids = [...values.primary];
          const index = ids.indexOf(e.target.value);
          ids.splice(index, 1);
          val = ids;
        } else {
          if (
            values.primary_assignee_id &&
            values.primary_assignee_id === e.target.value
          ) {
            dispatch(
              AddErrorAlert({
                err: {
                  message: "Cannot add task lead as additional assignee",
                },
              }),
            );
            setSkipSaveAlert(true);
          } else {
            val = ids = [...values.primary, e.target.value];
          }
        }
      } else {
        val = e.target.value;
      }

      if (prop === "content") {
        val = val.slice(0, MAX_DESCRIPTION_LENGTH);
      }

      setValues((vals) => ({ ...vals, [prop]: val }));
      if (prop !== "content" || (!isEmpty(val) && val.trim().length !== 0)) {
        setChanged(true);
      }
    },
    [dispatch, values],
  );

  // fetch subtasks when task modal is open
  React.useEffect(() => {
    getSubtasks(id, DEFAULT_EMBEDS)
      .then((subtasks) => setExistingSubtasks(subtasks))
      .catch((err) => console.error(err));
  }, [id]);

  // Update tasks from cache
  React.useEffect(() => {
    setExistingSubtasks((old) => {
      return old.map((subtask) => {
        const existing = tasks[subtask.id];
        if (!existing) {
          return { ...subtask };
        }

        // Pull only fields that can be seen / updated in the table
        const toUpdate = {
          title: existing.title ?? subtask.title,
          due_date: existing.due_date ?? subtask.due_date,
          status_id: existing.status_id ?? subtask.status_id,
          assignee_ids: existing.assignee_ids ?? subtask.assignee_ids,
        };
        return { ...subtask, ...toUpdate };
      });
    });
  }, [tasks]);

  // show || collapse the customfield area depending on subtasks status
  React.useEffect(() => {
    setCustomFieldsExpanded(!isSubTask);
  }, [isSubTask]);

  // submitting subtasks or tags where onChange event is one character per time
  const handleFields = (e, field, index, tag) => {
    setChanged(true);
    setShowUpdateMsg(false);
    setModifiedTask(false);
    let val;
    // submitting subtasks and tags on enter
    if (e.keyCode === 13) {
      if (field === "subtasks") {
        if (!subtaskTitle.length) {
          return;
        }

        setPendingAdd((old) => [...old, subtaskTitle]);
        setSubtaskTitle("");
        setShouldSave(true);
      } else if (field === "tags") {
        val = [...values[field], e.target.value];
        sendValues(val, field);
      }

      // deleting tags and subtasks
    } else if (index >= 0) {
      if (field !== "tags") {
        const newList = [...pendingAdd];
        newList.splice(index, 1);
        setPendingAdd(newList);
        setShouldSave(true);
      } else {
        let arr = [...values[field]];
        arr.splice(index, 1);
        sendValues(arr, field);
      }
    } else if (e === "noClick") {
      val = [...values[field], tag];
      sendValues(val, field);
    }
  };

  const sendValues = (val, field) => {
    setShouldSave(true);
    setChanged(true);
    setValues((vals) => ({ ...vals, [field]: val }));
    if (field === "client") {
      setNewUserName(null);
    }
  };

  // sets an array of existing subtasks to remove on submit
  const pendingDelete = (i) => {
    setChanged(true);
    setModifiedTask(false);

    if (pending.includes(i)) {
      let vals = [...pending];
      let index = pending.indexOf(i);
      vals.splice(index, 1);
      setPending(vals);
    } else {
      setPending([...pending, i]);
    }
  };

  const handleUpload = React.useCallback(
    async (data) => {
      try {
        setShowUpdateMsg(true);
        await updateTask(id, data);

        setModifiedTask(true);
        // Stagger updates to allow animation of save button
        setTimeout(() => {
          setShowUpdateMsg(false);
        }, 300);
      } catch (err) {
        const oldTaskContent = tasks[id];

        oldTaskContent && setValues(formatTask(oldTaskContent));
        setShowUpdateMsg(false);
        setModifiedTask(true);
        console.error("File Upload failed in TimelineTask", err);
        dispatch(AddErrorAlert({ err }));
      }
    },
    [updateTask, id, tasks, dispatch],
  );

  const removeTask = React.useCallback(async () => {
    try {
      await deleteSingleTask(id);
      history.goBack();
      setTimeout(() => updateTasks(tab, task), 2000);
    } catch (err) {
      console.error("Error deleting task", err);
    }
  }, [deleteSingleTask, history, id, tab, task, updateTasks]);

  const delayUpload = React.useCallback(() => {
    if (!subtaskTitle) {
      return;
    }

    if (subtaskTitle.length > 150) {
      dispatch(
        AddErrorAlert({
          err: { message: "Title cannot be more than 150 characters" },
        }),
      );
      return;
    }

    setPendingAdd((old) => [...old, subtaskTitle]);
    setSubtaskTitle("");
    setShouldSave(true);
  }, [subtaskTitle, dispatch]);

  const handleCommentSubmit = React.useCallback(
    async (comment) => {
      const newComment = await addComment(id, comment);

      if (!newComment) {
        return [false];
      }

      return [true, { ...newComment, task_id: id }];
    },
    [addComment, id],
  );

  const onRecordClick = React.useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  const handleAttachFileClick = React.useCallback(() => {
    inputFileRef?.current?.click();
  }, [inputFileRef]);

  const handleDetailsMenuSelect = React.useCallback(
    (name) => {
      if (name === ATTACH_FILE) {
        handleAttachFileClick();
      } else if (name === ATTACH_AUDIO) {
        onRecordClick();
      }
    },
    [handleAttachFileClick, onRecordClick],
  );

  const goToParentTask = () => {
    // Add parent to list, after this
    popFrame();
    history.push(`${Routes.TASKS}/${values.parent_id}`);
  };

  // Navigates away from this page after user denies saving
  React.useEffect(() => {
    if (changed || !navigate) {
      return;
    }

    history.push(navigate);
  }, [changed, navigate, history]);

  // Intercepts navigation event and shows 'Are you sure?' modal
  const handleNavigation = React.useCallback(
    (location, action) => {
      const path = location.pathname;

      if (typeof path !== "string") {
        return true;
      }

      const splitted = path.split("/");
      if (!splitted.length) {
        return true;
      }

      const lastPart = splitted.at(-1);
      const isTaskDetails =
        path.startsWith(`${Routes.TASKS}/`) && isUUID(lastPart);
      if (!isTaskDetails) {
        resetList();
      } else {
        if (action === "POP") {
          popOnBack({
            next: lastPart,
          });
        }
      }

      if (changed) {
        setTitle("Are you sure?");
        setReason("You have unsaved changes");
        setSubtext("Continuing will discard those changes");
        setAction(() => {
          setChanged(false);
          setNavigate(path);
        });
        setV2(true);
        return false;
      }

      return true;
    },
    [
      changed,
      resetList,
      setTitle,
      setReason,
      setSubtext,
      setAction,
      setV2,
      popOnBack,
    ],
  );

  // resets modifiedTask and changed when task id changes
  React.useEffect(() => {
    setModifiedTask(true);
    setChanged(false);
  }, [id]);

  // loads in users within the task if they're not in user provider
  React.useEffect(() => {
    if (values) {
      // 4 fields that display users [client, assignee_ids, watchers_ids, author]
      const { client, assignee_ids, watcher_ids, author } = values;

      loadUser(client);

      if (assignee_ids?.length) {
        assignee_ids.forEach((id) => {
          loadUser(id);
        });
      }

      if (watcher_ids?.length) {
        watcher_ids.forEach((id) => {
          loadUser(id);
        });
      }

      loadUser(author);
      loadUser(currentUser()?.id);
    }
  }, [values, loadUser, currentUser]);

  const saveTask = React.useCallback(
    async (vals = values) => {
      await handleTaskUpsert(
        vals,
        false,
        false,
        newUserName,
        false,
        id,
        false,
        pending,
        setValues,
        setPending,
        false,
        false,
        setChanged,
        noop,
        false,
        false,
        false,
        false,
        handleUpload,
        pendingAdd,
        setExistingSubtasks,
        setPendingAdd,
        uploadUser,
        [],
      );

      // Only save values with UUID keys and convert empty strings to null
      const saveableMeta = meta
        .filter((obj) => isUUID(obj.key))
        .map(({ key, value, row, row_key }) => {
          let val;
          if (value instanceof Date) {
            val = value.toISOString();
          } else {
            val = value.toString().trim();
            if (val.length === 0) {
              val = null;
            }
          }

          if (row_key) {
            return { key, value: val, row, row_key };
          }

          return { key, value: val };
        });
      // Save meta
      if (id) {
        dispatch(saveMeta(id, saveableMeta));
      }

      if (!skipSaveAlert) {
        dispatch(AddSuccessAlert("Changes saved"));
        setSkipSaveAlert(false);
      }
    },
    [
      values,
      handleTaskUpsert,
      newUserName,
      id,
      pending,
      handleUpload,
      pendingAdd,
      uploadUser,
      meta,
      skipSaveAlert,
      dispatch,
    ],
  );

  const duplicateTask = React.useCallback(
    async (vals = values) => {
      const { id, media = [], due_date, ...rest } = vals;
      const taskNotes = media.filter((m) =>
        m.filename.endsWith(FILE_EXTENSION),
      );
      await handleTaskUpsert(
        {
          ...rest,
          due_date: new Date(),
          media: taskNotes,
          title: `${rest.title} (Copy)`,
        },
        false,
        false,
        newUserName,
        false,
        undefined,
        false,
        pending,
        setValues,
        setPending,
        false,
        false,
        setChanged,
        noop,
        false,
        noop,
        true,
        true,
        handleUpload,
        pendingAdd,
        setExistingSubtasks,
        setPendingAdd,
        uploadUser,
        existingSubtasks,
      );

      dispatch(AddSuccessAlert("Changes saved"));
    },
    [
      values,
      handleTaskUpsert,
      newUserName,
      pending,
      handleUpload,
      pendingAdd,
      uploadUser,
      dispatch,
      existingSubtasks,
    ],
  );

  const markComplete = React.useCallback(
    (status) => {
      setValues((vals) => {
        const newVals = {
          ...vals,
          status: status.id,
          completed_at: new Date().toISOString(),
        };
        saveTask(newVals);
        return newVals;
      });
    },
    [saveTask],
  );

  const handleSelect = React.useCallback(
    async (opt) => {
      if (opt === DELETE) {
        setSubtext("");
        setTitle("Confirmation");
        setReason("Are you sure you wish to delete this task?");
        setV2(true);
        setAction(() => {
          removeTask();
        });

        return;
      }

      if (opt === SAVE) {
        saveTask();
        return;
      }

      if (opt === DUPLICATE) {
        await duplicateTask();
        return;
      }

      if (opt === SHARE) {
        setShowShare(true);
        return;
      }

      if (opt === CONVERT_TO_MAIN_TASK) {
        setSubtext("");
        setTitle("Confirmation");
        setReason("Are you sure you wish to convert this task to a main task?");
        setV2(true);
        setAction(() => {
          handleChange("parent_id")({ target: { value: null } });
        });
        return;
      }

      if (opt === COPY) {
        navigator.clipboard.writeText(
          `${window.location.origin}${window.location.pathname}`,
        );
        dispatch(AddSuccessAlert("Link copied to clipboard"));
        return;
      }

      if (opt === FAVOURITE) {
        await dispatch(addFavorite(user?.id, { item_id: id, type: "task" }));
        return;
      }

      if (opt === UNFAVOURITE) {
        await dispatch(removeFavorite(user?.id, id));
        return;
      }

      if (opt === COMPLETE) {
        const completeStatus = statuses.find(
          (status) => status.base_status === "completed",
        );
        if (completeStatus) {
          markComplete(completeStatus);
        }
        return;
      }

      if (opt === CLOSE) {
        await markCloseTask(id, false);
        if (isFavorite) {
          await dispatch(removeFavorite(user?.id, id));
        }
        return;
      }
    },
    [
      user,
      id,
      statuses,
      isFavorite,
      setSubtext,
      setTitle,
      setReason,
      setAction,
      setV2,
      removeTask,
      saveTask,
      duplicateTask,
      dispatch,
      markComplete,
      markCloseTask,
      handleChange,
    ],
  );

  // Perform save automatically
  React.useEffect(() => {
    if (!shouldSave || !changed) {
      return;
    }

    const timeout = setTimeout(() => {
      setShouldSave(false);
      saveTask();
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [shouldSave, changed, saveTask]);

  //If user came from url, but is not logged in, save id to redirect after login.
  React.useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!isLoggedIn) {
      localStorage.setItem("task-redirect-after-login", id);
      history.push(Routes.LOGIN);
    }
  }, [isLoading, isLoggedIn, history, id]);

  const handleEmailDrop = React.useCallback(
    async ({ id }) => {
      try {
        const { file } = emails[id];

        // Add the file to the task
        setValues((vals) => ({
          ...vals,
          file: [...(vals.file ?? []), file],
        }));

        // Trigger save
        setShouldSave(true);
        setChanged(true);
        setModifiedTask(true);
      } catch (err) {
        dispatch(AddErrorAlert({ err }));
      }
    },
    [emails, dispatch],
  );

  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: EMAIL_DRAG_TYPE,
    drop: handleEmailDrop,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      isDragging: monitor.getItemType() === EMAIL_DRAG_TYPE,
    }),
  });

  const saveButton = (className, iconClass) => (
    <SaveButton
      onClick={() => saveTask()}
      disabled={modifiedTask}
      className={className}
      loading={showUpdateMsg}
      loadingPosition="start"
      startIcon={<Send size={theme.size.sm} />}
    >
      Save
    </SaveButton>
  );

  const buttonMenu = (
    <div ref={menuRef} data-testid="task-menu-options" className={classes.menu}>
      <ButtonMenu
        id="options"
        options={menuOptions}
        onSelect={handleSelect}
        origin={{
          anchor: {
            vertical: "bottom",
            horizontal: "right",
          },
          transform: {
            vertical: "top",
            horizontal: "right",
          },
        }}
        targetRef={menuRef}
      >
        <More
          style={{ transform: "rotate(90deg)" }}
          className={classes.menuIcon}
        />
      </ButtonMenu>
    </div>
  );

  const handleFieldBlur = React.useCallback(() => {
    setShouldSave(true);
  }, []);

  return (
    <>
      <AgentPage rootRef={dropRef} scroll centerContent>
        {(isOver || isDragging) && <EmailDrop isOver={isOver} text="task" />}

        <div
          id="task-details"
          className={classes.root}
          style={{ position: "relative" }}
        >
          {notFound ? (
            <PermissionErrorPage skipAgentPage />
          ) : !taskFound ? (
            <LoadingSpinner />
          ) : (
            <>
              <TaskArrows
                onChange={(newID) => {
                  history.replace(`${Routes.TASKS}/${newID}`);
                }}
              />

              <div
                onClick={() => {
                  history.goBack();
                }}
                className={classes.backButton}
              >
                <ArrowLeft2 className="icon" />
                Go back
              </div>

              {parentName && (
                <div onClick={goToParentTask} className={classes.parentTitle}>
                  {parentName}
                </div>
              )}

              <div style={{ position: "relative" }}>
                <div className={clsx(classes.textField, classes.padded)}>
                  <input
                    placeholder="Give your task a name..."
                    value={values?.title}
                    onChange={handleChange("title")}
                    onBlur={() => setShouldSave(true)}
                    className={clsx(classes.input, "text")}
                    disabled={!canEdit}
                  />
                </div>

                {canEdit ? (
                  isSmall ? (
                    <div className={classes.headerButtons}>{buttonMenu}</div>
                  ) : (
                    <HeaderButtons
                      isFavorite={isFavorite}
                      markComplete={markComplete}
                      hideGoto
                      disableComplete={disableComplete}
                      alreadyComplete={isComplete}
                      title={
                        canUpdate || canEdit
                          ? null
                          : "You don't have permission to close this task"
                      }
                      canClose={canClose}
                      hasAllSubtasksClosed={hasAllSubtasksClosed}
                      saveButton={(className, iconClass) => (
                        <>
                          {saveButton(className, iconClass)}

                          {buttonMenu}

                          <FavoriteButton
                            isFavorite={isFavorite}
                            itemId={task?.id}
                            type="task"
                          />

                          <PinButton id={id} type="task" title={task?.title} />
                        </>
                      )}
                      canEdit={canEdit}
                      isClosed={Boolean(values.closed_at)}
                    />
                  )
                ) : (
                  <HeaderInfo />
                )}
              </div>

              <div className={classes.section}>
                {!detailsExpanded && (
                  <div className={classes.sectionHeader}>SUMMARY</div>
                )}

                {detailsExpanded && (
                  <CombinedTaskMeta
                    values={values}
                    currentUser={currentUser}
                    categories={categories}
                    types={types}
                    statuses={statuses}
                    priorities={priorities}
                    handleChange={handleChange}
                    searchUser={searchUser}
                    sendValues={sendValues}
                    setModifiedTask={setModifiedTask}
                    canComplete={canComplete}
                    canEdit={canEdit}
                    setNewUserName={setNewUserName}
                    newUserName={newUserName}
                    canSearchUser={canSearchUser}
                    canAddClient={canAddClient}
                    canUpdate={canUpdate}
                    teams={teams}
                    isSubTask={isSubTask}
                  />
                )}

                <div className={classes.sectionButton}>
                  <CollapseButton
                    open={detailsExpanded}
                    onToggle={() => setDetailsExpanded(!detailsExpanded)}
                  />
                </div>
              </div>

              {hasCustomFields && (
                <div
                  className={clsx(classes.section, classes.customFieldsSection)}
                >
                  {!customFieldsExpanded && (
                    <div className={classes.sectionHeader}>CUSTOM FIELDS</div>
                  )}

                  {customFieldsExpanded && (
                    <CustomFieldsSection
                      id={id}
                      setParentMeta={setMeta}
                      setChanged={setChanged}
                      setModifiedTask={setModifiedTask}
                      canEdit={canEdit}
                      onBlur={handleFieldBlur}
                      type={values.type_id}
                    />
                  )}

                  <div className={classes.sectionButton}>
                    <CollapseButton
                      open={customFieldsExpanded}
                      onToggle={() =>
                        setCustomFieldsExpanded(!customFieldsExpanded)
                      }
                    />
                  </div>
                </div>
              )}

              {!isSubTask && (
                <div className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <div>TAGS</div>
                  </div>

                  {tagsExpanded && (
                    <TaskTagsSection
                      canEdit={canEdit}
                      handleFields={handleFields}
                      values={values}
                    />
                  )}

                  <div className={classes.sectionButton}>
                    <CollapseButton
                      open={tagsExpanded}
                      onToggle={() => setTagsExpanded(!tagsExpanded)}
                    />
                  </div>
                </div>
              )}

              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <div className={classes.headerWithActions}>
                    {canEdit && (
                      <ButtonMenu
                        options={detailsMenuOpts}
                        onSelect={handleDetailsMenuSelect}
                      >
                        <AddCircle
                          className={classes.buttonMenuIcon}
                          color={theme.palette.primary.main}
                          size={theme.size.base}
                        />
                      </ButtonMenu>
                    )}

                    {!descriptionExpanded && "DESCRIPTION & NOTES & EMAILS"}
                  </div>
                </div>

                {descriptionExpanded && (
                  <TaskDescription
                    values={values}
                    handleChange={handleChange}
                    MAX_DESCRIPTION_LENGTH={MAX_DESCRIPTION_LENGTH}
                    setValues={setValues}
                    setModifiedTask={setModifiedTask}
                    canEdit={canEdit}
                    setChanged={setChanged}
                    onBlur={() => setShouldSave(true)}
                    isSubTask={isSubTask}
                    canSwitch={!changed}
                  />
                )}

                <div className={classes.sectionButton}>
                  <CollapseButton
                    open={descriptionExpanded}
                    onToggle={() =>
                      setDescriptionExpanded(!descriptionExpanded)
                    }
                  />
                </div>
              </div>

              {!isSubTask && (
                <div className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <div>
                      <Hierarchy
                        size={theme.size.sm}
                        variant="Bulk"
                        color="grey"
                        className={clsx("icon", classes.branchIcon)}
                      />
                      SUBTASKS
                      {isNihalife && (
                        <div
                          onClick={() =>
                            setShowClosedTask((old) =>
                              old === "true" ? "false" : "true",
                            )
                          }
                          className={classes.closedTaskVisibility}
                        >
                          <Tooltip
                            title={
                              showClosedTask === "true"
                                ? "Hide closed tasks"
                                : "Show closed tasks"
                            }
                            placement="top"
                          >
                            {showClosedTask === "false" ? (
                              <EyeSlash size={theme.size.sm} className="icon" />
                            ) : (
                              <Eye size={theme.size.sm} className="icon" />
                            )}
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>

                  {subAreaExpanded && (
                    <SubTaskArea
                      existingSubtasks={existingSubtasks}
                      setExistingSubtasks={setExistingSubtasks}
                      queueForDelete={pendingDelete}
                      pendingDelete={pending}
                      pendingAdd={pendingAdd}
                      handleFields={handleFields}
                      showInput={showInput}
                      subtaskTitle={subtaskTitle}
                      setSubtaskTitle={setSubtaskTitle}
                      delayUpload={delayUpload}
                      setShowInput={setShowInput}
                      setModifiedTask={setModifiedTask}
                      setChanged={setChanged}
                      setPending={setPending}
                      canEdit={canEdit}
                      showClosedTask={!isNihalife || showClosedTask === "true"}
                    />
                  )}

                  <div className={classes.sectionButton}>
                    <CollapseButton
                      open={subAreaExpanded}
                      onToggle={() => setSubAreaExpanded(!subAreaExpanded)}
                    />
                  </div>
                </div>
              )}

              <div
                className={classes.section}
                style={{
                  borderBottom: "none",
                }}
              >
                <div className={classes.sectionHeader}>
                  COMMENTS &amp; UPDATES
                </div>

                {commentsExpanded && (
                  <TaskComments
                    comments={comments}
                    submitComment={handleCommentSubmit}
                    canComment={canView}
                    taskID={values.id}
                  />
                )}

                <div className={classes.sectionButton}>
                  <CollapseButton
                    open={commentsExpanded}
                    onToggle={() => setCommentsExpanded(!commentsExpanded)}
                  />
                </div>
              </div>
            </>
          )}
          {isSmall && taskFound && saveButton(classes.saveButton, "icon")}
        </div>
      </AgentPage>

      <Prompt message={handleNavigation} when />
      {showShare && (
        <ShareTaskModal
          selected={task.id}
          handleOnClose={() => setShowShare(false)}
        />
      )}
    </>
  );
};

export default TaskDetailsPage;
